import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { documentSearch } from 'shared/services/railDocumentsApi';
import { DocumentSearchQuery, DocumentSearchResult } from 'shared/models/api/models';
import { documentSearchState, DocumentSearchState, SearchStateParams } from './DocumentSearchState';

import SubHeader from "components/menu/SubHeader";
import { documentTypeDisplay } from 'shared/enums/document-type-enum';
import { documentModuleDisplay, DocumentModuleEnum } from 'shared/enums/document-module-enum';
import { encodeTerm, decodeTerm } from 'components/portal/Search';
import { RefineSearch } from './RefineSearch';

import './DocumentSearch.css';

import {
    AnnouncementOutlined as AcknowledgeIcon,
    Star as FavouriteIcon,
    StarOutline as NotFavouriteIcon,
    InsertDriveFile as DocumentsIcon,
    HighlightOff as CloseIcon,
    ChevronRight as RightIcon,
    List as ListIcon,
} from '@mui/icons-material';
import { ReactComponent as RbNetworkRulesIcon } from "assets/icons/rb-network-rules.svg";

import { ReactComponent as StatusCurrentIcon } from "assets/icons/status-current.svg";
import { ReactComponent as StatusExpiredIcon } from "assets/icons/status-expired.svg";
import { ReactComponent as StatusRescindedIcon } from "assets/icons/action-fields-outlined.svg";
import { ReactComponent as StatusDeletedIcon } from "assets/icons/status-deleted.svg";
import { ReactComponent as StatusArchivedIcon } from "assets/icons/status-expired-folder.svg";

import { ReactComponent as RulebookIcon } from "assets/icons/rb-rulebook-filled.svg";
import { ReactComponent as MarkChatReadIcon } from "assets/icons/mark-chat-read.svg";
import { ReactComponent as PdfIcon } from "assets/icons/icon-pdf.svg";
import { ReactComponent as WorkOnTrackIcon } from "assets/icons/icon-work-on-track.svg";
import { DocumentStatusEnum } from 'shared/enums/document-status-enum';
import { getUserExistingRulebookMode } from '../../../../shared/store/applicationState';


interface StatusIconProps {
    status: DocumentStatusEnum
}

const StatusIcon = (props: StatusIconProps) => {

    if (props.status == DocumentStatusEnum.Current) {
        return (<StatusCurrentIcon />);
    }

    if (props.status == DocumentStatusEnum.Archived) {
        return (<StatusArchivedIcon />);
    }

    if (props.status == DocumentStatusEnum.Expired) {
        return (<StatusExpiredIcon />);
    }

    if (props.status == DocumentStatusEnum.Rescinded) {
        return (<StatusRescindedIcon />);
    }

    if (props.status == DocumentStatusEnum.Deleted) {
        return (<StatusDeletedIcon />);
    }

    return (<>{DocumentStatusEnum[props.status]}</>);
}


interface ResultProps {
    result: DocumentSearchResult;
    hideType?: boolean;
}

const DocumentCard = (props: ResultProps) => {
    const [result] = useState(props.result);
    const [searchTerm, setSearchTerm] = useState('');

    const dateTimeFormat = new Intl.DateTimeFormat("en-AU", {});

    const mode = getUserExistingRulebookMode();
    const documentLink = `/pdf/view/${mode}/${result.documentId}/page/${result.pageNumber}/search/${searchTerm}`;

    const escapeHtml = (text: string): string => {
        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    const escapeRegExp = (term: string) => {
        return term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    }

    const formatMatchText = (input: string, searchTerm: string): string => {
        if (input.length < 1) {
            return '';
        }

        let output = escapeHtml(input);
        const escapeSearch = escapeHtml(searchTerm);

        //Truncate multiple whitespaces down to a single space
        output = output.replace(/\s+/g, ' ');
        searchTerm = searchTerm.replace(/\s+/g, ' ');

        const matchWidth = 200; //How many characters to include both leading and following the first match

        if (searchTerm.length < 1) {
            return output.substring(0, matchWidth * 2);
        }

        let matchIsAtStart = false;
        let matchIsAtEnd = false;

        //Find where to start trimming
        let firstMatchPos = output.toLowerCase().indexOf(searchTerm.toLowerCase());
        let trimStartPosition = firstMatchPos - matchWidth;
        if (trimStartPosition < 0) {
            matchIsAtStart = true;
            trimStartPosition = 0;
        }

        //Find where to trimming until
        let trimLastPosition = trimStartPosition + matchWidth * 2;
        if (trimLastPosition > output.length) {
            matchIsAtEnd = true;
            trimLastPosition = output.length - 1;
        }

        //Trim output to matchWidth around the first matching term
        output = output.substring(trimStartPosition, trimLastPosition);

        //Remove partial trailing word
        let whitespaceMatches = [...output.matchAll(/\s/g)];
        var lastWhitespacePosition = whitespaceMatches[whitespaceMatches.length - 1].index;
        if (!matchIsAtEnd && typeof lastWhitespacePosition === 'number') {
            output = `${output.substring(0, lastWhitespacePosition)}&#8230;`;
        }

        //Remove partial leading word
        var firstWhitespacePosition = whitespaceMatches[0].index;
        if (!matchIsAtStart && typeof firstWhitespacePosition === 'number') {
            output = `&#8230;${output.substring(firstWhitespacePosition, output.length - 1)}`;
        }

        //Strip out match start and end characters
        const matchStart = String.fromCharCode(17);
        const matchEnd = String.fromCharCode(19);
        output = output.replaceAll(matchStart, '').replace(matchEnd, '');

        //Mark any instances of the search term matched
        let terms = escapeSearch.split(' ');
        terms.forEach(function (term) {
            term = escapeRegExp(term);
            let r = new RegExp('(\s?[a-zA-Z]*' + term + '[a-zA-Z]*\s?)', "ig");
            output = output.replaceAll(r, `${matchStart}$1${matchEnd}`);
        });

        //Inject the html after all search terms have been matched to prevent the html itself being matched
        output = output.replaceAll(matchStart, '<label class="match-term">');
        output = output.replaceAll(matchEnd, '</label>');

        return output;
    }

    useEffect(() => {
        const sub = documentSearchState.subscribe((s: DocumentSearchState) => {
            setSearchTerm(s.query.searchTerm);
        });
        return () => { sub.unsubscribe(); };
    }, []);

    return (
        <>
            <Link className="no-text-decoration document-search-card-link col" to={documentLink}>
                <div className="document-search-card">
                    <div className="document-search-card-body">
                        <div className="work-on-track-icon"><WorkOnTrackIcon width="1.5rem" height="1.5rem" /></div>
                        <div className="document-search-card-title">{result.documentTitle}</div>
                        <div><label className="document-search-card-label">Document number:</label> {result.documentNumber}</div>
                        {props.hideType !== true &&
                            <div><label className="document-search-card-label">Type:</label> {documentTypeDisplay(result.typeId)}</div>
                        }
                        <div><label className="document-search-card-label">Published:</label> {dateTimeFormat.format(new Date(result.publishedDate))}</div>
                        <div className="document-search-text-match" dangerouslySetInnerHTML={{ __html: formatMatchText(result.matchText, searchTerm) }} />
                        <div><label className="document-search-card-label">Total matches in the document:</label> {result.totalResults}</div>
                    </div>

                    <div className="document-search-card-footer">
                        {result.requiresAcknowledgement === true ?
                            <div className="document-search-card-icon document-search-card-acknowledge document-search-card-incomplete"><AcknowledgeIcon /></div>
                            :
                            <div className="document-search-card-icon document-search-card-acknowledge"><MarkChatReadIcon /></div>
                        }
                        {result.favourite === true ?
                            <div className="document-search-card-icon document-search-card-favourite"><FavouriteIcon /></div>
                            :
                            <div className="document-search-card-icon document-search-card-favourite"><NotFavouriteIcon /></div>
                        }
                        <div className="document-search-card-icon document-search-card-status"><StatusIcon status={result.status} /></div>
                        <div className="pdf-icon"><PdfIcon width="2.5rem" height="2.5rem" /></div>
                    </div>
                </div>
            </Link>
        </>
    );
}

interface GroupResultTitleParams {
    moduleId: number;
    typeId?: number;
}

export const GroupResultTitle = (props: GroupResultTitleParams) => {

    return (
        <>
            <div className="result-group-icon">
                {props.moduleId == DocumentModuleEnum.Rulebook ?
                    <RulebookIcon />
                    :
                    <DocumentsIcon />
                }
            </div>
            <div className="result-group-title">{documentModuleDisplay(props.moduleId)}</div>

            {props.typeId &&
                <>
                    <div className="result-spacer-icon"><RightIcon /></div>
                    <div className="result-group-icon"><RbNetworkRulesIcon /></div>
                    <div className="result-group-title">{documentTypeDisplay(props.typeId)}</div>
                </>
            }
        </>
    );
}

interface GroupResultsParams {
    results: DocumentSearchResult[];
    groupName: string;
    children: React.ReactNode;
    hideType?: boolean;
}

export const GroupResults = (props: GroupResultsParams) => {
    const defaultResults = 3;
    const [maxResults, setMaxResults] = useState(defaultResults);

    const displayAllResults = () => {
        setMaxResults(props.results.length);
    }

    const displayDefaultResults = () => {
        setMaxResults(Math.min(defaultResults, props.results.length));
    }

    useEffect(() => {
        displayDefaultResults();
    }, [props]);

    return (
        <>
            {props.results.length > 0 &&
                <div className={`container document-search-results document-search-${props.groupName}`}>

                    <div>
                        <div className="document-search-results-header">{props.children}</div>
                        {maxResults < props.results.length &&
                            <div className="document-search-results-header document-search-results-header-right see-all" onClick={displayAllResults}>See all +</div>
                        }
                        {maxResults > defaultResults &&
                            <div className="document-search-results-header document-search-results-header-right see-all" onClick={displayDefaultResults}>See all -</div>
                        }
                        <div className="document-search-results-header document-search-results-header-right">{maxResults} of {props.results.length} documents</div>
                    </div>

                    <div className="d-inline-block w-100">
                        <div className="row">
                            {props.results.map((result, i) => (
                                <React.Fragment key={`${props.groupName}-${i}-${Date.now()}`}>
                                    {i < maxResults &&
                                        <DocumentCard result={result} hideType={props.hideType} />
                                    }
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            }
        </>
    );
}

interface MatchParams {
    searchTerm: string;
}

export const DocumentSearch = () => {
    const params = useParams();
    const location = useLocation();
    const [results, setResults] = useState([] as DocumentSearchResult[]);
    const [rulebookResults, setRulebookResults] = useState([] as DocumentSearchResult[]);
    const [documentResults, setDocumentResults] = useState([] as DocumentSearchResult[]);
    const [typeResults, setTypeResults] = useState([] as DocumentSearchResult[][]);

    const [searchQuery, setSearchQuery] = useState({ searchTerm: decodeTerm(params.searchTerm!) } as DocumentSearchQuery);
    const [isSearching, setIsSearching] = useState(true);
    const [isPreSearch, setIsPreSearch] = useState(true);
    const [mounted, setMounted] = useState(false);
    const [stateParams] = useState(location.state as SearchStateParams);

    const minSearchTermLength = 2;

    const navigate = useNavigate();

    const navigatePrevious = () => {
        if (stateParams != undefined && typeof (stateParams.from) === 'string') {
            navigate(stateParams.from);
        }
        else {
            navigate("/");
        }
    }

    const triggerSearch = () => {
        setIsPreSearch(false);
        documentSearchState.setState(q => {
            q.query = searchQuery;
            q.runQuery = true;
        });
    }

    const search = (query: DocumentSearchQuery) => {
        if (query.searchTerm === undefined || query.searchTerm.length < minSearchTermLength) {
            setIsSearching(false);
            return;
        }

        setResults([]);
        setIsSearching(true);

        const mode = getUserExistingRulebookMode();
        query.mode = mode!;
        documentSearch(query)
            .then(results => {
                searchResults(results);
            });
    }

    const searchResults = (results: DocumentSearchResult[]) => {
        setIsSearching(false);
        setRulebookResults(results.filter((doc: DocumentSearchResult) => doc.moduleId === 1));
        setDocumentResults(results.filter((doc: DocumentSearchResult) => doc.moduleId !== 1));
        splitTypeResults(results);
        setResults(results);
    }

    const splitTypeResults = (results: DocumentSearchResult[]) => {
        let types = [] as DocumentSearchResult[][];
        results.forEach((result) => {
            if (types[result.typeId] === undefined) {
                types[result.typeId] = [] as DocumentSearchResult[];
            }
            types[result.typeId].push(result);
        });
        setTypeResults(types);
    }

    const queryModuleCount = (): number => {
        let count = 0;
        if (searchQuery.moduleIds !== undefined) {
            count = searchQuery.moduleIds.length;
        }
        return count;
    }

    const queryTypeCount = (): number => {
        let count = 0;
        if (searchQuery.typeIds !== undefined) {
            count = searchQuery.typeIds.length;
        }
        return count;
    }

    useEffect(() => {
        const mount = async () => {
            if (mounted === true) {
                return;
            }
            setMounted(true);

            if (searchQuery.searchTerm.length > minSearchTermLength - 1) {
                triggerSearch();
            }
            else {
                setSearchQuery({ ...searchQuery, searchTerm: '' });
            }
        }

        mount();

        const sub = documentSearchState.subscribe((s: DocumentSearchState) => {
            setSearchQuery(s.query);

            if (s.pushHistory === true) {
                const searchState: SearchStateParams = { from: location.pathname }
                navigate(`/search/${encodeTerm(s.query.searchTerm)}`, searchState);
            }

            if (s.runQuery === true) {
                setIsPreSearch(false);
                search(s.query);
            }

            if (s.pushHistory === true || s.runQuery === true) {
                documentSearchState.setState(q => {
                    q.pushHistory = false;
                    q.runQuery = false;
                });
            }

        });

        return () => {
            sub.unsubscribe();
        };
    }, [results]);

    return (
        <>
            <div className="search-body-bg">

                <div className="search-header-bg"></div>

                <div className="narrow-container">

                    <SubHeader hideSearchBar={true} />

                    <div className="document-search-page">
                        <div className="document-search-container">

                            <div className="search-results-header">Search results</div>
                            <div className="search-close" onClick={navigatePrevious}><CloseIcon /></div>

                            <RefineSearch query={searchQuery} minSearchTermLength={minSearchTermLength} />

                            {!isPreSearch &&
                                <>
                                    {isSearching ?
                                        <div className="total-search-results" >Searching...</div>
                                        :
                                        <>
                                            <div className="total-search-results">{results.length} total documents found</div>
                                            {(queryModuleCount() > 0 || queryTypeCount() > 0) ?
                                                <>
                                                    {typeResults.map((type, i) => (
                                                        <GroupResults key={`group-result-${i}`} results={type} groupName={`type-${type[0].typeId}`} hideType={true}>
                                                            <GroupResultTitle moduleId={type[0].moduleId} typeId={type[0].typeId} />
                                                        </GroupResults>
                                                    ))}
                                                </>
                                                :
                                                <>
                                                    <GroupResults results={rulebookResults} groupName={"rulebook"}>
                                                        <GroupResultTitle moduleId={DocumentModuleEnum.Rulebook} />
                                                    </GroupResults>
                                                    <GroupResults results={documentResults} groupName={"documents"}>
                                                        <GroupResultTitle moduleId={DocumentModuleEnum.Document} />
                                                    </GroupResults>
                                                </>
                                            }

                                        </>

                                    }
                                </>
                            }

                        </div>

                        <div className="document-search-footer"></div>

                    </div>

                </div>

            </div>
        </>
    );
}

export default DocumentSearch;
